import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Wallet, Bell, BellOff, Edit, Trash2, AlertTriangle, CheckCircle, Calendar, DollarSign, TrendingDown, Briefcase, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';
import { toast } from 'sonner';

function Custos() {
  const [custos, setCustos] = useState([]);
  const [lembretes, setLembretes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCusto, setEditingCusto] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [proLabore, setProLabore] = useState({ percentual: 0, ativo: false, base: 'mes_atual', faturamento_base: 0, valor_calculado: 0, faturamento_mes_ref: '' });
  const [proLaboreOpen, setProLaboreOpen] = useState(false);
  const [proLaboreForm, setProLaboreForm] = useState({ percentual: '0', ativo: false, base: 'mes_atual' });
  const [savingProLabore, setSavingProLabore] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'fixo',
    nome: '',
    valor: '',
    categoria: '',
    dia_vencimento: '',
    lembrete_ativo: false,
    dias_antes_lembrete: 3,
    descricao: '',
    ativo: true
  });

  const categoriasSugeridas = [
    'Aluguel', 'Energia', 'Água', 'Internet', 'Telefone', 
    'Contador', 'Impostos', 'Seguros', 'Manutenção', 'Material de Limpeza',
    'Combustível', 'Fornecedores', 'Marketing', 'Software', 'Outros'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [custosRes, lembretesRes, proLaboreRes] = await Promise.all([
        api.get('/custos'),
        api.get('/custos/lembretes/hoje'),
        api.get('/pro-labore'),
      ]);
      setCustos(custosRes.data);
      setLembretes(lembretesRes.data);
      setProLabore(proLaboreRes.data);
      setProLaboreForm({
        percentual: String(proLaboreRes.data.percentual ?? 0),
        ativo: !!proLaboreRes.data.ativo,
        base: proLaboreRes.data.base || 'mes_atual',
      });
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    }
  };

  const salvarProLabore = async () => {
    const pct = parseFloat(proLaboreForm.percentual);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Percentual deve estar entre 0 e 100');
      return;
    }
    setSavingProLabore(true);
    try {
      const { data } = await api.put('/pro-labore', {
        percentual: pct,
        ativo: proLaboreForm.ativo,
        base: proLaboreForm.base,
      });
      setProLabore(data);
      toast.success(data.ativo
        ? `Pró-Labore de ${pct}% ativo: ${formatCurrency(data.valor_calculado)}`
        : 'Pró-Labore desativado');
      setProLaboreOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar Pró-Labore');
    } finally {
      setSavingProLabore(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        valor: parseFloat(formData.valor) || 0,
        dia_vencimento: formData.dia_vencimento ? parseInt(formData.dia_vencimento) : null,
        dias_antes_lembrete: parseInt(formData.dias_antes_lembrete) || 3
      };

      if (editingCusto) {
        await api.put(`/custos/${editingCusto.id}`, dataToSend);
        toast.success('Custo atualizado com sucesso');
      } else {
        await api.post('/custos', dataToSend);
        toast.success('Custo cadastrado com sucesso');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar custo');
      console.error(error);
    }
  };

  const handleEdit = (custo) => {
    setEditingCusto(custo);
    setFormData({
      tipo: custo.tipo,
      nome: custo.nome,
      valor: custo.valor,
      categoria: custo.categoria,
      dia_vencimento: custo.dia_vencimento || '',
      lembrete_ativo: custo.lembrete_ativo,
      dias_antes_lembrete: custo.dias_antes_lembrete || 3,
      descricao: custo.descricao || '',
      ativo: custo.ativo
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este custo?')) {
      try {
        await api.delete(`/custos/${id}`);
        toast.success('Custo excluído com sucesso');
        loadData();
      } catch (error) {
        toast.error('Erro ao excluir custo');
        console.error(error);
      }
    }
  };

  const toggleLembrete = async (custo) => {
    try {
      await api.put(`/custos/${custo.id}`, {
        lembrete_ativo: !custo.lembrete_ativo
      });
      toast.success(custo.lembrete_ativo ? 'Lembrete desativado' : 'Lembrete ativado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar lembrete');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'fixo',
      nome: '',
      valor: '',
      categoria: '',
      dia_vencimento: '',
      lembrete_ativo: false,
      dias_antes_lembrete: 3,
      descricao: '',
      ativo: true
    });
    setEditingCusto(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const custosFiltrados = custos.filter(c => {
    if (filtroTipo === 'todos') return true;
    return c.tipo === filtroTipo;
  });

  const custosFixos = custos.filter(c => c.tipo === 'fixo' && c.ativo);
  const custosVariaveis = custos.filter(c => c.tipo === 'variavel' && c.ativo);
  const totalFixo = custosFixos.reduce((acc, c) => acc + c.valor, 0);
  const totalVariavel = custosVariaveis.reduce((acc, c) => acc + c.valor, 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="custos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1">Custos</h1>
          <p className="text-sm text-slate-500">Gerencie custos fixos e variáveis</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            onClick={() => setProLaboreOpen(true)}
            className={`rounded-sm flex-1 sm:flex-none ${proLabore.ativo ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-white/5 hover:bg-white/10 border border-white/10'} text-white`}
            data-testid="pro-labore-btn"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            Pró-Labore
            {proLabore.ativo && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white/20">{proLabore.percentual}%</span>}
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm w-full sm:w-auto" data-testid="add-custo-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Custo
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-carbon border-white/10 text-white max-w-[95vw] sm:max-w-lg mx-2">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold uppercase">
                {editingCusto ? 'Editar Custo' : 'Novo Custo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tipo */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, tipo: 'fixo' })}
                  className={`py-3 px-4 rounded-sm border transition-all text-sm ${
                    formData.tipo === 'fixo'
                      ? 'bg-electric-blue border-electric-blue text-white'
                      : 'bg-carbon border-white/10 text-slate-400 hover:border-white/20'
                  }`}
                >
                  <Wallet className="w-4 h-4 inline mr-2" />
                  Fixo
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, tipo: 'variavel' })}
                  className={`py-3 px-4 rounded-sm border transition-all text-sm ${
                    formData.tipo === 'variavel'
                      ? 'bg-electric-blue border-electric-blue text-white'
                      : 'bg-carbon border-white/10 text-slate-400 hover:border-white/20'
                  }`}
                >
                  <TrendingDown className="w-4 h-4 inline mr-2" />
                  Variável
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Nome</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="Ex: Aluguel"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Valor</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder=""
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Categoria</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                  <SelectTrigger className="bg-obsidian border-white/10 text-white">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white max-h-48">
                    {categoriasSugeridas.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Dia Vencimento</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.dia_vencimento}
                    onChange={(e) => setFormData({ ...formData, dia_vencimento: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="Ex: 10"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Lembrar (dias antes)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={formData.dias_antes_lembrete}
                    onChange={(e) => setFormData({ ...formData, dias_antes_lembrete: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    placeholder="3"
                  />
                </div>
              </div>

              {/* Lembrete */}
              <div className="flex items-center justify-between bg-obsidian p-4 rounded-md border border-white/10">
                <div className="flex items-center gap-3">
                  <Bell className={`w-5 h-5 ${formData.lembrete_ativo ? 'text-electric-blue' : 'text-slate-500'}`} />
                  <div>
                    <p className="text-sm text-white">Ativar lembrete</p>
                    <p className="text-xs text-slate-500">Receba alerta antes do vencimento</p>
                  </div>
                </div>
                <Switch
                  checked={formData.lembrete_ativo}
                  onCheckedChange={(checked) => setFormData({ ...formData, lembrete_ativo: checked })}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Descrição (opcional)</Label>
                <Input
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="Observações..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                  {editingCusto ? 'Salvar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Info Pró-Labore ativo */}
      {proLabore.ativo && proLabore.percentual > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          data-testid="pro-labore-banner"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Briefcase className="w-6 h-6 text-emerald-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-300">
                Pró-Labore ativo: {proLabore.percentual}% do faturamento
              </p>
              <p className="text-xs text-slate-400">
                Base: {proLabore.base === 'mes_anterior' ? 'mês anterior' : 'mês atual'} ({proLabore.faturamento_mes_ref}) · Faturamento: {formatCurrency(proLabore.faturamento_base || 0)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase text-emerald-300 tracking-wider">Valor calculado</p>
            <p className="text-2xl font-bold text-emerald-400" data-testid="pro-labore-valor-atual">
              {formatCurrency(proLabore.valor_calculado || 0)}
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => loadData()}
            className="text-emerald-300 hover:bg-emerald-500/10"
            title="Recalcular agora"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </motion.div>
      )}

      {/* Lembretes Ativos */}
      {lembretes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-safety-orange/10 border border-safety-orange/30 rounded-md p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-safety-orange flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-safety-orange mb-2">Lembretes de Pagamento</h3>
              <div className="space-y-2">
                {lembretes.map((l) => (
                  <div key={l.id} className="flex items-center justify-between bg-black/20 p-2 rounded">
                    <div>
                      <span className="text-white text-sm">{l.nome}</span>
                      <span className="text-slate-400 text-xs ml-2">- {l.categoria}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white font-semibold text-sm">{formatCurrency(l.valor)}</span>
                      <span className={`text-xs px-2 py-1 rounded ${l.urgente ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {l.dias_ate_vencimento === 0 ? 'Vence HOJE' : 
                         l.dias_ate_vencimento === 1 ? 'Vence amanhã' : 
                         `Vence em ${l.dias_ate_vencimento} dias`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Custos Fixos</p>
            <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-electric-blue" />
          </div>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{formatCurrency(totalFixo)}</p>
          <p className="text-xs text-slate-500 mt-1">{custosFixos.length} itens</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Custos Variáveis</p>
            <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-safety-orange" />
          </div>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{formatCurrency(totalVariavel)}</p>
          <p className="text-xs text-slate-500 mt-1">{custosVariaveis.length} itens</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="col-span-2 lg:col-span-1 bg-carbon border border-white/5 rounded-md p-4 sm:p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Total Mensal</p>
            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
          </div>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-500">{formatCurrency(totalFixo + totalVariavel)}</p>
          <p className="text-xs text-slate-500 mt-1">Previsão de saídas</p>
        </motion.div>
      </div>

      {/* Filtro */}
      <div className="flex items-center gap-2 mb-4">
        <Select value={filtroTipo} onValueChange={setFiltroTipo}>
          <SelectTrigger className="w-40 bg-carbon border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-carbon border-white/10 text-white">
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="fixo">Fixos</SelectItem>
            <SelectItem value="variavel">Variáveis</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista de Custos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {custosFiltrados.length === 0 ? (
          <div className="col-span-full bg-carbon border border-white/5 rounded-md p-8 sm:p-12 text-center">
            <Wallet className="w-10 h-10 sm:w-12 sm:h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-sm sm:text-base text-slate-500">Nenhum custo cadastrado</p>
          </div>
        ) : (
          custosFiltrados.map((custo) => (
            <motion.div
              key={custo.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`bg-carbon border rounded-md p-4 transition-colors ${
                custo.ativo ? 'border-white/5 hover:border-white/20' : 'border-white/5 opacity-50'
              }`}
              data-testid={`custo-card-${custo.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded ${
                      custo.tipo === 'fixo' 
                        ? 'bg-electric-blue/20 text-electric-blue' 
                        : 'bg-safety-orange/20 text-safety-orange'
                    }`}>
                      {custo.tipo === 'fixo' ? 'FIXO' : 'VARIÁVEL'}
                    </span>
                    {custo.pro_labore && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                        <Briefcase className="w-2.5 h-2.5" /> AUTO
                      </span>
                    )}
                    {custo.lembrete_ativo && (
                      <Bell className="w-3 h-3 text-electric-blue" />
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-white truncate">{custo.nome}</h3>
                  <p className="text-xs text-slate-500">{custo.categoria}</p>
                </div>
                <span className="text-base font-bold text-white">{formatCurrency(custo.valor)}</span>
              </div>

              {custo.dia_vencimento && (
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                  <Calendar className="w-3 h-3" />
                  <span>Vence dia {custo.dia_vencimento}</span>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                {custo.pro_labore ? (
                  <span className="text-[10px] text-emerald-400/70 italic px-1">
                    Editar em Pró-Labore →
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleLembrete(custo)}
                    className={`p-2 h-8 ${custo.lembrete_ativo ? 'text-electric-blue' : 'text-slate-500'}`}
                    title={custo.lembrete_ativo ? 'Desativar lembrete' : 'Ativar lembrete'}
                  >
                    {custo.lembrete_ativo ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                  </Button>
                )}
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => custo.pro_labore ? setProLaboreOpen(true) : handleEdit(custo)}
                    className="text-slate-400 hover:text-white hover:bg-white/10 p-2 h-8"
                    title={custo.pro_labore ? 'Editar percentual do Pró-Labore' : 'Editar'}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {!custo.pro_labore && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(custo.id)}
                      className="text-red-500 hover:text-white hover:bg-red-500/10 p-2 h-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Modal Configuração Pró-Labore */}
      <Dialog open={proLaboreOpen} onOpenChange={setProLaboreOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-md" data-testid="pro-labore-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-bold uppercase">
              <Briefcase className="w-5 h-5 text-emerald-400" /> Configurar Pró-Labore
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded p-3 text-xs text-emerald-200">
              Define uma % do faturamento como custo fixo. O valor é recalculado automaticamente conforme entrada de receitas.
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Percentual (%) *</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={proLaboreForm.percentual}
                  onChange={(e) => setProLaboreForm({ ...proLaboreForm, percentual: e.target.value })}
                  className="bg-obsidian border-white/10 text-white text-lg font-bold pr-8"
                  placeholder="Ex: 30"
                  data-testid="pro-labore-percentual"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
              </div>
              <div className="flex gap-1 mt-1 flex-wrap">
                {[10, 20, 30, 40, 50].map((p) => (
                  <Button
                    key={p}
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setProLaboreForm({ ...proLaboreForm, percentual: String(p) })}
                    className="text-[10px] h-6 px-2 text-slate-400 hover:bg-white/5 hover:text-white"
                  >
                    {p}%
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Base de cálculo</Label>
              <Select value={proLaboreForm.base} onValueChange={(v) => setProLaboreForm({ ...proLaboreForm, base: v })}>
                <SelectTrigger className="bg-obsidian border-white/10 text-white" data-testid="pro-labore-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-carbon border-white/10 text-white">
                  <SelectItem value="mes_atual">Faturamento do mês atual</SelectItem>
                  <SelectItem value="mes_anterior">Faturamento do mês anterior</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Preview em tempo real */}
            <div className="bg-obsidian border border-white/10 rounded p-3">
              <p className="text-[10px] uppercase text-slate-500 tracking-wider">Preview do cálculo</p>
              <div className="flex items-baseline justify-between mt-1">
                <div>
                  <p className="text-xs text-slate-400">
                    Faturamento ({proLabore.faturamento_mes_ref || '—'})
                  </p>
                  <p className="text-sm text-white">{formatCurrency(proLabore.faturamento_base || 0)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">Pró-Labore</p>
                  <p className="text-xl font-bold text-emerald-400" data-testid="pro-labore-preview">
                    {formatCurrency(
                      (proLabore.faturamento_base || 0) *
                      (parseFloat(proLaboreForm.percentual) || 0) / 100
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between bg-obsidian p-4 rounded-md border border-white/10">
              <div>
                <p className="text-sm text-white">Ativo</p>
                <p className="text-xs text-slate-500">Cria/atualiza custo fixo automaticamente</p>
              </div>
              <Switch
                checked={proLaboreForm.ativo}
                onCheckedChange={(v) => setProLaboreForm({ ...proLaboreForm, ativo: v })}
                data-testid="pro-labore-ativo"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setProLaboreOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button
                onClick={salvarProLabore}
                disabled={savingProLabore}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="pro-labore-salvar"
              >
                {savingProLabore ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Custos;
